import React, { useEffect, useReducer, useRef, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useSectionEditor } from "./SectionEditorContext";
import {
  QUIZ_EXAMPLE_BANK,
  QUIZ_QUESTION_TYPES,
} from "../../../../data/quizExampleBank";

const createId = (prefix = "question") =>
  `${prefix}-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;

const createQuestion = (type = "short-answer", overrides = {}) => {
  const id = overrides.id || createId("question");
  const base = {
    id,
    type,
    prompt: "",
    score: 1,
    feedback: {
      correct: "",
      incorrect: "",
    },
    allowPartialCredit: false,
  };

  switch (type) {
    case "picklist":
      return {
        ...base,
        options: [],
        correctValue: "",
        ...overrides,
      };
    case "true-false":
      return {
        ...base,
        correctValue: true,
        ...overrides,
      };
    case "short-answer":
    default:
      return {
        ...base,
        expectedResponse: "",
        ...overrides,
      };
  }
};

export const createInitialQuizState = (metadata = {}) => {
  const questions = Array.isArray(metadata.questions)
    ? metadata.questions.map((question) =>
        createQuestion(question.type || "short-answer", question)
      )
    : [];

  return { questions };
};

export const quizSectionReducer = (state, action) => {
  switch (action.type) {
    case "HYDRATE": {
      if (!action.payload || typeof action.payload !== "object") {
        return state;
      }
      const nextState = createInitialQuizState(action.payload);
      const currentSerialized = JSON.stringify(state);
      const nextSerialized = JSON.stringify(nextState);
      return currentSerialized === nextSerialized ? state : nextState;
    }
    case "ADD_QUESTION": {
      const questionType = action.questionType || "short-answer";
      return {
        ...state,
        questions: [...state.questions, createQuestion(questionType)],
      };
    }
    case "REMOVE_QUESTION": {
      if (!action.id) {
        return state;
      }
      return {
        ...state,
        questions: state.questions.filter((question) => question.id !== action.id),
      };
    }
    case "UPDATE_QUESTION": {
      const { id, updates } = action;
      if (!id || !updates || typeof updates !== "object") {
        return state;
      }
      return {
        ...state,
        questions: state.questions.map((question) =>
          question.id === id ? { ...question, ...updates } : question
        ),
      };
    }
    case "LOAD_EXAMPLE": {
      const { questionType, example } = action;
      if (!questionType || !example) {
        return state;
      }
      const question = createQuestion(questionType, {
        ...example,
        id: createId("example"),
      });
      return {
        ...state,
        questions: [...state.questions, question],
      };
    }
    case "UPDATE_OPTION": {
      const { questionId, optionIndex, updates } = action;
      if (!questionId || optionIndex == null) {
        return state;
      }
      return {
        ...state,
        questions: state.questions.map((question) => {
          if (question.id !== questionId || !Array.isArray(question.options)) {
            return question;
          }
          const nextOptions = question.options.map((option, index) =>
            index === optionIndex ? { ...option, ...updates } : option
          );
          return { ...question, options: nextOptions };
        }),
      };
    }
    case "ADD_OPTION": {
      const { questionId } = action;
      if (!questionId) {
        return state;
      }
      return {
        ...state,
        questions: state.questions.map((question) => {
          if (question.id !== questionId || !Array.isArray(question.options)) {
            return question;
          }
          const nextOptions = [
            ...question.options,
            { label: "Option", value: `option-${question.options.length + 1}` },
          ];
          return { ...question, options: nextOptions };
        }),
      };
    }
    case "REMOVE_OPTION": {
      const { questionId, optionIndex } = action;
      if (!questionId || optionIndex == null) {
        return state;
      }
      return {
        ...state,
        questions: state.questions.map((question) => {
          if (question.id !== questionId || !Array.isArray(question.options)) {
            return question;
          }
          const nextOptions = question.options.filter(
            (_, index) => index !== optionIndex
          );
          const nextCorrectValue = nextOptions.find(
            (option) => option.value === question.correctValue
          )
            ? question.correctValue
            : "";
          return {
            ...question,
            options: nextOptions,
            correctValue: nextCorrectValue,
          };
        }),
      };
    }
    default:
      return state;
  }
};

const computeQuestionErrors = (question) => {
  const errors = {};
  if (!question.prompt || !question.prompt.trim()) {
    errors.prompt = "Prompt is required.";
  }

  if (Number.isNaN(Number(question.score)) || Number(question.score) <= 0) {
    errors.score = "Score must be greater than zero.";
  }

  if (!question.feedback?.correct || !question.feedback.correct.trim()) {
    errors.feedbackCorrect = "Enter feedback for correct responses.";
  }

  if (!question.feedback?.incorrect || !question.feedback.incorrect.trim()) {
    errors.feedbackIncorrect = "Enter feedback for incorrect responses.";
  }

  switch (question.type) {
    case "short-answer":
      if (!question.expectedResponse || !question.expectedResponse.trim()) {
        errors.expectedResponse = "Provide an exemplar response or guidance.";
      }
      break;
    case "picklist": {
      if (!Array.isArray(question.options) || question.options.length < 2) {
        errors.options = "Add at least two answer options.";
      }
      if (!question.correctValue || !question.correctValue.toString().trim()) {
        errors.correctValue = "Choose the correct option.";
      }
      break;
    }
    case "true-false":
      if (typeof question.correctValue !== "boolean") {
        errors.correctValue = "Set the correct true/false answer.";
      }
      break;
    default:
      break;
  }

  return errors;
};

const computeQuizErrors = (state) => {
  if (!Array.isArray(state.questions) || state.questions.length === 0) {
    return { questions: "Add at least one question." };
  }

  const questionErrors = state.questions.map((question) =>
    computeQuestionErrors(question)
  );

  const hasErrors = questionErrors.some(
    (errors) => Object.keys(errors).length > 0
  );

  return hasErrors ? { questionErrors } : null;
};

const QuizSectionEditor = () => {
  const { section, updateMetadata, setSectionErrors, sectionErrors } =
    useSectionEditor();
  const [state, dispatch] = useReducer(
    quizSectionReducer,
    section.metadata,
    createInitialQuizState
  );
  const serializedRef = useRef(JSON.stringify(state));
  const lastErrorRef = useRef(null);
  const [exampleSelection, setExampleSelection] = useState("");

  useEffect(() => {
    dispatch({ type: "HYDRATE", payload: section.metadata });
  }, [section.metadata]);

  useEffect(() => {
    const serialized = JSON.stringify(state);
    if (serializedRef.current !== serialized) {
      serializedRef.current = serialized;
      updateMetadata({ questions: state.questions });
    }
  }, [state, updateMetadata]);

  useEffect(() => {
    const errors = computeQuizErrors(state);
    const serializedErrors = JSON.stringify(errors || {});
    if (lastErrorRef.current !== serializedErrors) {
      lastErrorRef.current = serializedErrors;
      setSectionErrors(errors);
    }
  }, [state, setSectionErrors]);

  const handleExampleSelect = (event) => {
    const value = event.target.value;
    setExampleSelection(value);
    if (!value) {
      return;
    }
    const [type, index] = value.split(":");
    const examples = QUIZ_EXAMPLE_BANK[type] || [];
    const example = examples[Number(index)];
    if (example) {
      dispatch({ type: "LOAD_EXAMPLE", questionType: type, example });
    }
    setTimeout(() => setExampleSelection(""), 0);
  };

  const handleQuestionChange = (id, updates) => {
    dispatch({ type: "UPDATE_QUESTION", id, updates });
  };

  const renderQuestionBody = (question) => {
    switch (question.type) {
      case "picklist":
        return (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2">Answer options</Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  dispatch({ type: "ADD_OPTION", questionId: question.id })
                }
              >
                Add option
              </Button>
            </Stack>
            {sectionErrors?.questionErrors?.length &&
              sectionErrors.questionErrors[state.questions.indexOf(question)]?.options && (
                <Typography color="error" variant="body2">
                  {
                    sectionErrors.questionErrors[state.questions.indexOf(question)]
                      .options
                  }
                </Typography>
              )}
            <Stack spacing={1}>
              {(question.options || []).map((option, optionIndex) => (
                <Stack
                  key={`${question.id}-option-${optionIndex}`}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <TextField
                    label="Option label"
                    value={option.label || ""}
                    fullWidth
                    onChange={(event) =>
                      dispatch({
                        type: "UPDATE_OPTION",
                        questionId: question.id,
                        optionIndex,
                        updates: { label: event.target.value },
                      })
                    }
                  />
                  <TextField
                    label="Value"
                    value={option.value || ""}
                    onChange={(event) =>
                      dispatch({
                        type: "UPDATE_OPTION",
                        questionId: question.id,
                        optionIndex,
                        updates: { value: event.target.value },
                      })
                    }
                  />
                  <IconButton
                    aria-label="Remove option"
                    onClick={() =>
                      dispatch({
                        type: "REMOVE_OPTION",
                        questionId: question.id,
                        optionIndex,
                      })
                    }
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <TextField
              select
              label="Correct option"
              value={question.correctValue || ""}
              onChange={(event) =>
                handleQuestionChange(question.id, {
                  correctValue: event.target.value,
                })
              }
              helperText={
                sectionErrors?.questionErrors?.length &&
                sectionErrors.questionErrors[state.questions.indexOf(question)]
                  ?.correctValue
              }
              error={Boolean(
                sectionErrors?.questionErrors?.length &&
                  sectionErrors.questionErrors[state.questions.indexOf(question)]
                    ?.correctValue
              )}
            >
              <MenuItem value="">Select option</MenuItem>
              {(question.options || []).map((option, optionIndex) => (
                <MenuItem key={`${question.id}-correct-${optionIndex}`} value={option.value}>
                  {option.label || option.value || `Option ${optionIndex + 1}`}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        );
      case "true-false":
        return (
          <FormControl component="fieldset">
            <FormControlLabel
              control={
                <Switch
                  checked={question.correctValue === true}
                  onChange={(event) =>
                    handleQuestionChange(question.id, {
                      correctValue: event.target.checked,
                    })
                  }
                />
              }
              label={
                question.correctValue === true
                  ? "Correct answer: True"
                  : "Correct answer: False"
              }
            />
          </FormControl>
        );
      case "short-answer":
      default:
        return (
          <TextField
            label="Expected response"
            value={question.expectedResponse || ""}
            multiline
            minRows={2}
            onChange={(event) =>
              handleQuestionChange(question.id, {
                expectedResponse: event.target.value,
              })
            }
            helperText={
              sectionErrors?.questionErrors?.length &&
              sectionErrors.questionErrors[state.questions.indexOf(question)]
                ?.expectedResponse
            }
            error={Boolean(
              sectionErrors?.questionErrors?.length &&
                sectionErrors.questionErrors[state.questions.indexOf(question)]
                  ?.expectedResponse
            )}
          />
        );
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h6" component="h3">
            Quiz questions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Build formative questions with scoring, feedback, and quick examples
            from the bank.
          </Typography>
        </Box>

        <TextField
          select
          label="Example bank"
          value={exampleSelection}
          onChange={handleExampleSelect}
          helperText="Insert a prebuilt example question"
        >
          <MenuItem value="">Select an example</MenuItem>
          {Object.entries(QUIZ_EXAMPLE_BANK).map(([type, examples]) =>
            examples.map((example, index) => (
              <MenuItem value={`${type}:${index}`} key={`${type}:${index}`}>
                {QUIZ_QUESTION_TYPES.find((item) => item.value === type)?.label ||
                  type}
                {" — "}
                {example.prompt.length > 60
                  ? `${example.prompt.slice(0, 57)}…`
                  : example.prompt}
              </MenuItem>
            ))
          )}
        </TextField>

        {sectionErrors?.questions && (
          <Typography color="error" variant="body2">
            {sectionErrors.questions}
          </Typography>
        )}

        <Stack spacing={2}>
          {state.questions.map((question) => (
            <Paper
              key={question.id}
              variant="outlined"
              sx={{ p: 2.5, bgcolor: "background.default" }}
            >
              <Stack spacing={2.5}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {QUIZ_QUESTION_TYPES.find((item) => item.value === question.type)
                      ?.label || "Question"}
                  </Typography>
                  <IconButton
                    aria-label="Remove question"
                    onClick={() =>
                      dispatch({ type: "REMOVE_QUESTION", id: question.id })
                    }
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel id={`${question.id}-type-label`}>
                        Question type
                      </InputLabel>
                      <Select
                        labelId={`${question.id}-type-label`}
                        label="Question type"
                        value={question.type}
                        onChange={(event) =>
                          handleQuestionChange(question.id, {
                            ...createQuestion(event.target.value, question),
                            type: event.target.value,
                          })
                        }
                      >
                        {QUIZ_QUESTION_TYPES.map((item) => (
                          <MenuItem value={item.value} key={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField
                      label="Prompt"
                      value={question.prompt}
                      onChange={(event) =>
                        handleQuestionChange(question.id, {
                          prompt: event.target.value,
                        })
                      }
                      fullWidth
                      multiline
                      minRows={2}
                      helperText={
                        sectionErrors?.questionErrors?.length &&
                        sectionErrors.questionErrors[
                          state.questions.indexOf(question)
                        ]?.prompt
                      }
                      error={Boolean(
                        sectionErrors?.questionErrors?.length &&
                          sectionErrors.questionErrors[
                            state.questions.indexOf(question)
                          ]?.prompt
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={1.5}>
                    <TextField
                      label="Score"
                      type="number"
                      value={question.score}
                      onChange={(event) =>
                        handleQuestionChange(question.id, {
                          score: Number(event.target.value),
                        })
                      }
                      fullWidth
                      helperText={
                        sectionErrors?.questionErrors?.length &&
                        sectionErrors.questionErrors[
                          state.questions.indexOf(question)
                        ]?.score
                      }
                      error={Boolean(
                        sectionErrors?.questionErrors?.length &&
                          sectionErrors.questionErrors[
                            state.questions.indexOf(question)
                          ]?.score
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={1.5}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={question.allowPartialCredit || false}
                          onChange={(event) =>
                            handleQuestionChange(question.id, {
                              allowPartialCredit: event.target.checked,
                            })
                          }
                        />
                      }
                      label="Partial credit"
                    />
                  </Grid>
                </Grid>

                {renderQuestionBody(question)}

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Feedback (correct)"
                      value={question.feedback?.correct || ""}
                      onChange={(event) =>
                        handleQuestionChange(question.id, {
                          feedback: {
                            ...question.feedback,
                            correct: event.target.value,
                          },
                        })
                      }
                      fullWidth
                      helperText={
                        sectionErrors?.questionErrors?.length &&
                        sectionErrors.questionErrors[
                          state.questions.indexOf(question)
                        ]?.feedbackCorrect
                      }
                      error={Boolean(
                        sectionErrors?.questionErrors?.length &&
                          sectionErrors.questionErrors[
                            state.questions.indexOf(question)
                          ]?.feedbackCorrect
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Feedback (incorrect)"
                      value={question.feedback?.incorrect || ""}
                      onChange={(event) =>
                        handleQuestionChange(question.id, {
                          feedback: {
                            ...question.feedback,
                            incorrect: event.target.value,
                          },
                        })
                      }
                      fullWidth
                      helperText={
                        sectionErrors?.questionErrors?.length &&
                        sectionErrors.questionErrors[
                          state.questions.indexOf(question)
                        ]?.feedbackIncorrect
                      }
                      error={Boolean(
                        sectionErrors?.questionErrors?.length &&
                          sectionErrors.questionErrors[
                            state.questions.indexOf(question)
                          ]?.feedbackIncorrect
                      )}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Paper>
          ))}
        </Stack>

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => dispatch({ type: "ADD_QUESTION" })}
        >
          Add question
        </Button>
      </Stack>
    </Paper>
  );
};

export default QuizSectionEditor;
